import React from 'react';
import {
  LegacyMasonry,
  Masonry,
  wsDriver,
  getMasonryWSRootNode,
} from '@leagueplatform/masonry';
import { CarePageWrapper } from '@leagueplatform/care-common';
import { useIntl } from '@leagueplatform/locales';
import {
  usePageViewAnalytics,
  PRODUCT_AREA,
  SCREEN_NAMES,
} from '@leagueplatform/analytics';
import { MasonryEngine } from '@leagueplatform/masonry-engine';
import {
  useDocumentTitle,
  getScopedLegacyCssClassNames,
} from '@leagueplatform/web-common';
import {
  Responsive,
  StackLayout,
  Box,
  HeadingText,
} from '@leagueplatform/genesis-core';
import { PageContainer } from '@leagueplatform/web-common-components';
import { useCareDiscoveryConfig } from '../../care-discovery.config';

const masonryConfig = {
  apiVersion: 1,
  productArea: PRODUCT_AREA.GET_CARE,
  screenName: SCREEN_NAMES.HOMEPAGE,
};

const GetCareMasonryLayoutWrapper = ({
  RightColumn,
  LeftColumn,
}: {
  RightColumn?: React.ReactNode;
  LeftColumn: React.ReactNode;
}) =>
  RightColumn ? (
    <>
      {/* Mobile: RightColumn top + LeftColumn bottom */}
      <Responsive down="tablet">
        <StackLayout spacing="$two">
          <Box css={{ width: '100%' }}>{RightColumn}</Box>
          <Box css={{ width: '100%' }}>{LeftColumn}</Box>
        </StackLayout>
      </Responsive>
      {/* Tablet and up: LeftColumn left + RightColumn right */}
      <Responsive up="tablet">
        <StackLayout orientation="horizontal" spacing="$two">
          <Box
            css={{
              width: '70%',
              '@tablet': { width: '60%' },
            }}
          >
            {LeftColumn}
          </Box>
          <Box
            css={{
              width: '30%',
              '@tablet': { width: '40%' },
            }}
          >
            {RightColumn}
          </Box>
        </StackLayout>
      </Responsive>
    </>
  ) : (
    React.createElement(React.Fragment, null, LeftColumn)
  );

// Masonry V1 Container
// This container is rendered by default, unless a masonry app version is provided
const DefaultMasonryContainer = ({
  masonryAppId,
}: {
  masonryAppId: { right?: string; left: string };
}) => {
  const LeftColumn = React.useMemo(
    () =>
      new LegacyMasonry({
        ...{
          apiVersion: masonryConfig.apiVersion,
          analytics: {
            productArea: masonryConfig.productArea,
            screenName: masonryConfig.screenName,
          },
        },
        appId: masonryAppId.left,
        apiParams: {
          platform: 'web',
        },
      }),
    [masonryAppId.left],
  );

  // Right column is optional, so it needs to be validated
  const RightColumn = React.useMemo(() => {
    if (masonryAppId.right) {
      return new LegacyMasonry({
        ...{
          apiVersion: masonryConfig.apiVersion,
          analytics: {
            productArea: masonryConfig.productArea,
            screenName: masonryConfig.screenName,
          },
        },
        appId: masonryAppId.right,
      });
    }
    return undefined;
  }, [masonryAppId.right]);

  return (
    <GetCareMasonryLayoutWrapper
      RightColumn={RightColumn && <RightColumn.Provider />}
      LeftColumn={<LeftColumn.Provider />}
    />
  );
};

// Masonry 1.5 Container
const MasonryV1Container = ({
  masonryAppId,
}: {
  masonryAppId: { right?: string; left: string };
}) => {
  const LeftRootNode = React.useMemo(
    () =>
      getMasonryWSRootNode({
        ...masonryConfig,
        appId: masonryAppId.left,
        apiParams: {
          platform: 'web',
        },
      }),
    [masonryAppId.left],
  );

  // Right column is optional, so it needs to be validated
  const RightRootNode = React.useMemo(() => {
    if (masonryAppId.right) {
      return getMasonryWSRootNode({
        ...masonryConfig,
        appId: masonryAppId.right,
      });
    }
    return undefined;
  }, [masonryAppId.right]);

  return (
    <GetCareMasonryLayoutWrapper
      RightColumn={
        RightRootNode && (
          <MasonryEngine driver={wsDriver} rootNode={RightRootNode} />
        )
      }
      LeftColumn={<MasonryEngine driver={wsDriver} rootNode={LeftRootNode} />}
    />
  );
};

// Masonry V2 Container
const MasonryV2Container = ({
  masonryAppId,
  initialQueryParams,
}: {
  masonryAppId: { right?: string; left: string };
  initialQueryParams?: Record<string, string>;
}) => (
  <Masonry
    rootNodeId={masonryAppId.left}
    initialQueryParams={initialQueryParams}
  />
);

export const MasonryGetCareLandingPage = () => {
  const { formatMessage } = useIntl();

  usePageViewAnalytics({
    product_area: PRODUCT_AREA.GET_CARE,
    sub_product_area: SCREEN_NAMES.HOMEPAGE,
    screen_name: SCREEN_NAMES.HOMEPAGE,
  });

  useDocumentTitle(formatMessage({ id: 'GET_CARE' }));

  const defaultMasonryAppId = { left: '', right: '' };

  const {
    masonryAppId = defaultMasonryAppId,
    masonryAppVersion,
    getCareLandingMasonryQueryParams,
  } = useCareDiscoveryConfig();

  switch (masonryAppVersion) {
    case '2.0':
      return (
        <PageContainer
          className={getScopedLegacyCssClassNames()}
          css={{
            '@mobile': {
              paddingInline: '$oneAndHalf',
            },
          }}
        >
          <StackLayout
            css={{
              marginTop: '$four',
              marginBottom: '$four',
              '@mobile': {
                marginTop: '$three',
                marginBottom: '$three',
              },
            }}
            spacing="$twoAndHalf"
            horizontalAlignment="stretch"
          >
            <HeadingText size="xxl" level="1">
              {formatMessage({ id: 'GET_CARE' })}
            </HeadingText>
            <MasonryV2Container
              masonryAppId={masonryAppId}
              initialQueryParams={getCareLandingMasonryQueryParams}
            />
          </StackLayout>
        </PageContainer>
      );
    case '1.5':
      return (
        <CarePageWrapper title={formatMessage({ id: 'GET_CARE' })}>
          <MasonryV1Container masonryAppId={masonryAppId} />
        </CarePageWrapper>
      );
    default:
      return (
        <CarePageWrapper title={formatMessage({ id: 'GET_CARE' })}>
          <DefaultMasonryContainer masonryAppId={masonryAppId} />
        </CarePageWrapper>
      );
  }
};
